import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Twitter, Linkedin, Globe, ShieldCheck, Loader2, Calendar, Clock, ArrowRight } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { fetchAuthor, fetchBlogs } from '../lib/api';
import Seo from '../components/Seo';
import { buildAuthorSchema } from '../lib/schema';
import NotFound from './NotFound';

const AuthorPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [author, setAuthor] = useState(undefined); // undefined=loading, null=not-found
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    fetchAuthor(slug)
      .then((a) => {
        setAuthor(a);
        fetchBlogs({ author_id: a.id }).then(setPosts).catch(() => setPosts([]));
      })
      .catch(() => setAuthor(null));
  }, [slug]);

  if (author === undefined) return <div className="min-h-screen grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-teal-600" /></div>;
  if (author === null) return <NotFound />;

  return (
    <div className="bg-slate-50 min-h-screen">
      <Seo
        title={author.meta_title || `${author.name} - Author at Learndunia`}
        description={author.meta_description || author.bio}
        image={author.avatar}
        canonical={`https://learndunia.com/author/${author.slug}`}
        jsonLd={buildAuthorSchema(author)}
      />

      <div className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 py-10">
          <button onClick={() => navigate(-1)} className="text-sm text-slate-600 hover:text-teal-700 inline-flex items-center gap-1 mb-6">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <div className="flex flex-col md:flex-row items-start gap-6">
            <img src={author.avatar} alt={author.name} className="w-24 h-24 md:w-32 md:h-32 rounded-2xl object-cover shadow-sm" />
            <div className="flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-3xl md:text-4xl font-bold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{author.name}</h1>
                {author.is_fact_checker && (
                  <Badge className="bg-teal-100 text-teal-800 hover:bg-teal-100 border-teal-200">
                    <ShieldCheck className="w-3 h-3 mr-1" /> Fact Checker
                  </Badge>
                )}
              </div>
              {author.designation && <p className="text-slate-600 mt-1">{author.designation}</p>}
              {author.bio && (
                <div className="prose prose-slate mt-4 max-w-none text-sm text-slate-700" dangerouslySetInnerHTML={{ __html: author.bio }} />
              )}
              <div className="flex flex-wrap gap-4 mt-5 text-sm">
                {author.email && <a href={`mailto:${author.email}`} className="inline-flex items-center gap-1 text-slate-600 hover:text-teal-700"><Mail className="w-4 h-4" /> {author.email}</a>}
                {author.twitter && <a href={`https://twitter.com/${author.twitter}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-slate-600 hover:text-teal-700"><Twitter className="w-4 h-4" /> @{author.twitter}</a>}
                {author.linkedin && <a href={`https://linkedin.com/in/${author.linkedin}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-slate-600 hover:text-teal-700"><Linkedin className="w-4 h-4" /> LinkedIn</a>}
                {author.website && <a href={author.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-slate-600 hover:text-teal-700"><Globe className="w-4 h-4" /> Website</a>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-10">
        <h2 className="text-2xl font-bold text-slate-900 mb-5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Articles by {author.name}</h2>
        {posts.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center text-slate-500 text-sm">No published articles yet.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {posts.map((b) => (
              <Link key={b.id} to={`/blog/${b.slug || b.id}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-200 card-lift flex">
                <div className="w-40 flex-shrink-0 overflow-hidden">
                  <img src={b.image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4 flex-1 min-w-0">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 font-semibold">{b.category}</span>
                    <span className="text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {b.readTime}</span>
                  </div>
                  <h3 className="mt-2 font-semibold text-slate-900 leading-snug group-hover:text-teal-700 line-clamp-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{b.title}</h3>
                  <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                    <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" /> {b.date}</span>
                    <span className="text-teal-700 font-semibold group-hover:text-orange-600 inline-flex items-center gap-1">Read <ArrowRight className="w-3 h-3" /></span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuthorPage;
